import { useQuery } from "@tanstack/react-query";
import { Link } from "@/lib/router-compat";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { ShoppingCart, ArrowRight } from "lucide-react";

interface Match {
  hub: string;
  slug: string;
  brand: string;
  model: string;
  image_url: string | null;
  price_gbp: number | null;
  rating: number | null;
  affiliate_link: string | null;
}

const TABLES = [
  { hub: "lawn-mowers", table: "lawn_mowers" as const },
  { hub: "generators", table: "generators" as const },
  { hub: "chainsaws", table: "chainsaws" as const },
  { hub: "pressure-washers", table: "pressure_washers" as const },
];

const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();

const extractModelNumber = (s: string): string | null => {
  const matches = s.toUpperCase().match(/\b[A-Z]{1,6}\d{2,}[A-Z]{0,6}\b/g);
  return matches ? matches[0] : null;
};

interface MatchingProductBoxProps {
  productName?: string;
  manualHub?: string | null;
  manualSlug?: string | null;
}

const HUB_TO_TABLE: Record<string, string> = {
  "lawn-mowers": "lawn_mowers",
  generators: "generators",
  chainsaws: "chainsaws",
  "pressure-washers": "pressure_washers",
  "impact-drivers": "impact_drivers",
};

const SELECT_COLS = "slug, brand, model, image_url, price_gbp, rating, affiliate_link";

export const MatchingProductBox = ({ productName, manualHub, manualSlug }: MatchingProductBoxProps) => {
  const { data: match } = useQuery<Match | null>({
    queryKey: ["article-product-match-v4", productName, manualHub, manualSlug],
    queryFn: async () => {
      if (manualHub && manualSlug) {
        const dedicatedTable = HUB_TO_TABLE[manualHub];
        if (dedicatedTable) {
          const { data } = await supabase
            .from(dedicatedTable as any)
            .select(SELECT_COLS)
            .eq("slug", manualSlug)
            .maybeSingle();
          if (data) {
            return { hub: manualHub, ...(data as any) };
          }
        } else {
          const { data } = await supabase
            .from("products" as any)
            .select(SELECT_COLS)
            .eq("slug", manualSlug)
            .eq("product_type", manualHub)
            .maybeSingle();
          if (data) {
            return { hub: manualHub, ...(data as any) };
          }
        }
        return null;
      }

      if (!productName) return null;
      const needle = norm(productName);
      if (needle.length < 4) return null;
      const tokens = needle.split(" ").filter((t) => t.length > 1);
      const modelNum = extractModelNumber(productName);

      for (const { hub, table } of TABLES) {
        if (modelNum) {
          const { data: direct } = await supabase
            .from(table as any)
            .select(SELECT_COLS)
            .eq("status", "published")
            .ilike("model", `%${modelNum}%`)
            .limit(5);
          if (direct?.length) {
            const best = (direct as any[]).reduce((a: any, b: any) => {
              const scoreA = norm(`${a.brand} ${a.model}`).split(" ").filter((t: string) => tokens.includes(t)).length;
              const scoreB = norm(`${b.brand} ${b.model}`).split(" ").filter((t: string) => tokens.includes(t)).length;
              return scoreB > scoreA ? b : a;
            });
            return { hub, ...(best as any) };
          }
        }

        const { data } = await supabase
          .from(table as any)
          .select(SELECT_COLS)
          .eq("status", "published")
          .or(tokens.slice(0, 3).map((t) => `model.ilike.%${t}%,brand.ilike.%${t}%`).join(","))
          .limit(20);

        if (!data?.length) continue;

        let best: { row: any; score: number } | null = null;
        for (const row of data as any[]) {
          const candidate = norm(`${row.brand} ${row.model}`);
          const candTokens = new Set(candidate.split(" "));
          const overlap = tokens.filter((t) => candTokens.has(t)).length;
          const score = overlap / Math.max(tokens.length, 1);
          if (score >= 0.5 && (!best || score > best.score)) {
            best = { row, score };
          }
        }
        if (best) {
          return { hub, ...(best.row as any) };
        }
      }
      return null;
    },
    staleTime: 60 * 60 * 1000,
  });

  if (!match) return null;

  const affiliate = match.affiliate_link;
  const internalHref = `/${match.hub}/${match.slug}`;

  const TitleWrap = ({ children }: { children: React.ReactNode }) =>
    affiliate ? (
      <a href={affiliate} target="_blank" rel="sponsored noopener noreferrer" className="hover:text-primary">
        {children}
      </a>
    ) : (
      <Link to={internalHref} className="hover:text-primary">{children}</Link>
    );

  const ImageWrap = ({ children }: { children: React.ReactNode }) =>
    affiliate ? (
      <a href={affiliate} target="_blank" rel="sponsored noopener noreferrer" className="shrink-0">
        {children}
      </a>
    ) : (
      <Link to={internalHref} className="shrink-0">{children}</Link>
    );

  return (
    <aside
      aria-label="Latest price and specs"
      className="my-8 rounded-xl border-2 border-primary/20 bg-primary/5 p-5 md:p-6"
    >
      <div className="flex flex-col sm:flex-row gap-5 items-start">
        {match.image_url && (
          <ImageWrap>
            <img
              src={match.image_url}
              alt={`${match.brand} ${match.model}`}
              className="w-32 h-32 object-contain bg-white rounded-lg p-2 border"
              loading="lazy"
            />
          </ImageWrap>
        )}
        <div className="flex-1 min-w-0">
          <p className="text-xs uppercase tracking-wide text-muted-foreground font-semibold mb-1">
            Latest price &amp; specs
          </p>
          <h3 className="text-lg md:text-xl font-bold text-foreground mb-1">
            <TitleWrap>
              {match.brand} {match.model}
            </TitleWrap>
          </h3>
          {match.price_gbp && (
            <p className="text-2xl font-bold text-foreground mb-3">
              From £{match.price_gbp.toLocaleString()}
            </p>
          )}
          {affiliate ? (
            <a href={affiliate} target="_blank" rel="sponsored noopener noreferrer">
              <Button size="sm" className="gap-2">
                <ShoppingCart className="h-4 w-4" />
                See latest price &amp; retailers
                <ArrowRight className="h-4 w-4" />
              </Button>
            </a>
          ) : (
            <Link to={internalHref}>
              <Button size="sm" className="gap-2">
                <ShoppingCart className="h-4 w-4" />
                See latest price &amp; retailers
                <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          )}
        </div>
      </div>
    </aside>
  );
};

export default MatchingProductBox;
